import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";
import TopBar from "../components/TopBar";
import TrainingModal from "../components/TrainingModal";
import AttendanceModal from "../components/AttendanceModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export default function CalendarPage() {
  const { t } = useLang();
  const password = localStorage.getItem("st_admin_pw") || "";
  const headers = { "X-Admin-Password": password };

  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [trainings, setTrainings] = useState([]);
  const [creating, setCreating] = useState(null); // {date}
  const [editing, setEditing] = useState(null);
  const [attendingId, setAttendingId] = useState(null);

  const months = t("months").split(",");
  const weekdays = t("weekdays").split(",");

  const range = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    return { from: ymd(first), to: ymd(last), first, last };
  }, [cursor]);

  const load = async () => {
    const r = await axios.get(`${API}/admin/trainings`, {
      headers,
      params: { date_from: range.from, date_to: range.to },
    });
    setTrainings(r.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [range.from, range.to]);

  // Build month grid (6 rows × 7 cols)
  const grid = useMemo(() => {
    const cells = [];
    const startWeekday = range.first.getDay();
    const daysInMonth = range.last.getDate();
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    while (cells.length < 42) cells.push(null);
    return cells;
  }, [cursor, range]);

  const byDate = useMemo(() => {
    const m = {};
    for (const tr of trainings) {
      (m[tr.date] = m[tr.date] || []).push(tr);
    }
    return m;
  }, [trainings]);

  const todayStr = ymd(new Date());

  const onSaved = (tr) => {
    setTrainings((arr) => {
      const idx = arr.findIndex((x) => x.id === tr.id);
      if (idx >= 0) {
        const c = [...arr];
        c[idx] = tr;
        return c;
      }
      return [...arr, tr];
    });
    setCreating(null);
    setEditing(null);
  };

  const onDeleted = (id) => {
    setTrainings((arr) => arr.filter((x) => x.id !== id));
    setEditing(null);
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-end justify-between mb-5 gap-3 flex-wrap">
          <div>
            <h1 className="font-bebas text-4xl sm:text-5xl tracking-[0.1em]">
              📅 {t("calendar")}
            </h1>
            <p className="text-sm text-neutral-400 mt-1">
              {t("upcoming_trainings")} · Soccer Troop
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))}
              data-testid="cal-prev"
              className="w-10 h-10 rounded-lg bg-[#161616] border border-[#252525] hover:border-[#e8559a]/50 text-white transition-colors"
            >
              ‹
            </button>
            <div className="font-bebas text-xl tracking-widest min-w-[180px] text-center">
              {months[cursor.getMonth()]} {cursor.getFullYear()}
            </div>
            <button
              onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))}
              data-testid="cal-next"
              className="w-10 h-10 rounded-lg bg-[#161616] border border-[#252525] hover:border-[#e8559a]/50 text-white transition-colors"
            >
              ›
            </button>
            <button
              onClick={() => setCursor(startOfMonth(new Date()))}
              className="ml-2 px-3 py-2 rounded-lg bg-[#161616] border border-[#252525] hover:border-[#e8559a]/50 text-neutral-300 text-sm transition-colors"
            >
              {t("today")}
            </button>
            <button
              onClick={() => setCreating({ date: todayStr })}
              data-testid="new-training-btn"
              className="ml-2 px-4 py-2 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] text-white font-bebas text-sm tracking-widest transition-colors"
            >
              ＋ {t("new_training")}
            </button>
          </div>
        </div>

        <div className="rounded-2xl bg-[#161616] border border-[#252525] overflow-hidden">
          <div className="grid grid-cols-7 text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-b border-[#252525]">
            {weekdays.map((w) => (
              <div key={w} className="px-2 py-2.5 text-center">{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7" data-testid="calendar-grid">
            {grid.map((d, i) => {
              if (!d) return <div key={i} className="min-h-[90px] sm:min-h-[110px] bg-[#0c0c0c] border-r border-b border-[#1a1a1a]" />;
              const dStr = ymd(d);
              const items = byDate[dStr] || [];
              const isToday = dStr === todayStr;
              return (
                <button
                  type="button"
                  key={i}
                  onClick={() => setCreating({ date: dStr })}
                  data-testid={`cal-cell-${dStr}`}
                  className={`min-h-[90px] sm:min-h-[110px] text-left border-r border-b border-[#1a1a1a] p-1.5 sm:p-2 hover:bg-[#1a1a1a] transition-colors group relative ${
                    isToday ? "bg-[#e8559a]/[0.06]" : ""
                  }`}
                >
                  <div className={`text-xs font-medium ${isToday ? "text-[#e8559a]" : "text-neutral-300"}`}>
                    {d.getDate()}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {items.slice(0, 3).map((tr) => (
                      <div
                        key={tr.id}
                        onClick={(e) => { e.stopPropagation(); setEditing(tr); }}
                        data-testid={`cal-training-${tr.id}`}
                        className="block px-1.5 py-0.5 rounded bg-[#e8559a]/15 border border-[#e8559a]/30 text-[#e8559a] text-[10px] truncate hover:bg-[#e8559a]/25"
                      >
                        {tr.time} {tr.title}
                      </div>
                    ))}
                    {items.length > 3 && (
                      <div className="text-[10px] text-neutral-500 px-1.5">+{items.length - 3}</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Upcoming list under calendar */}
        <div className="mt-6">
          <h2 className="font-bebas text-2xl tracking-widest text-white mb-3">
            📋 {t("upcoming_trainings")}
          </h2>
          <UpcomingList
            trainings={trainings.filter((tr) => tr.date >= todayStr)}
            onEdit={setEditing}
            onAttend={setAttendingId}
            t={t}
          />
        </div>
      </main>

      {(creating || editing) && (
        <TrainingModal
          initial={editing || creating}
          mode={editing ? "edit" : "create"}
          onClose={() => { setCreating(null); setEditing(null); }}
          onSaved={onSaved}
          onDeleted={onDeleted}
          onMarkAttendance={(id) => { setEditing(null); setAttendingId(id); }}
        />
      )}
      {attendingId && (
        <AttendanceModal
          trainingId={attendingId}
          onClose={() => setAttendingId(null)}
          onSaved={() => { setAttendingId(null); load(); }}
        />
      )}
    </div>
  );
}

function UpcomingList({ trainings, onEdit, onAttend, t }) {
  if (trainings.length === 0) {
    return (
      <div className="rounded-xl bg-[#161616] border border-[#252525] p-8 text-center text-neutral-500">
        {t("no_trainings")}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {trainings.map((tr) => (
        <div
          key={tr.id}
          className="rounded-xl bg-[#161616] border border-[#252525] p-4 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid={`upcoming-${tr.id}`}
        >
          <div className="flex-shrink-0 w-16 text-center rounded-lg bg-[#0f0f0f] border border-[#252525] py-2">
            <div className="font-bebas text-2xl text-[#e8559a] tracking-widest leading-none">
              {(tr.date || "").slice(8, 10)}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
              {(tr.date || "").slice(5, 7)}/{(tr.date || "").slice(0, 4)}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-white">{tr.title}</div>
            <div className="text-xs text-neutral-400 mt-1">
              🕒 {tr.time} · 📍 {tr.location || "—"} · ⏱ {tr.duration_minutes} min
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => onAttend(tr.id)}
              className="px-3 py-1.5 rounded-md bg-[#e8559a]/10 border border-[#e8559a]/30 text-[#e8559a] text-xs font-medium hover:bg-[#e8559a]/20 transition-colors"
            >
              ✓ {t("attendance")}
            </button>
            <button
              onClick={() => onEdit(tr)}
              className="px-3 py-1.5 rounded-md bg-[#1f1f1f] border border-[#333] hover:border-[#e8559a]/50 text-neutral-200 text-xs font-medium transition-colors"
            >
              ✎ {t("edit")}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
